import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Header from '../../../shared/components/Header';
import Footer from '../../../shared/components/Footer';
import Hero from '../components/Hero';
import ServicesMiddle from '../components/ServicesMiddle';
import Testimonials from '../components/Testimonials';
import DesignChooseArea from '../components/DesignChooseArea';
import WorkVideoArea from '../components/WorkVideoArea';
import PopularServices from '../components/PopularServices';
import DiscountedServices from '../components/DiscountedServices';
import ServiceVideosSection from '../components/ServiceVideosSection';
import { useCategories, toArray } from '../../services/hooks/useCatalog';

/* Continuous horizontal marquee ticker. The item list is duplicated and the
   track slides by exactly one copy (-50%), so it loops seamlessly with no gap
   or cut-off. Pauses on hover; respects reduced-motion (see index.css guard). */
const marqueeScroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const CategoryStrip = styled.div`
  background: ${p => (p.$variant === 'light' ? '#0a1a52' : '#082189')};
  overflow: hidden;
  width: 100%;
`;

const MarqueeTrack = styled.div`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 13px 0;
  will-change: transform;
  /* Never pauses — keeps scrolling continuously and seamlessly. */
  animation: ${marqueeScroll} ${p => p.$duration || 26}s linear infinite;

  @media (max-width: 480px) {
    padding: 11px 0;
  }
`;

const CategoryName = styled.span`
  flex-shrink: 0;
  color: ${p => (p.$promo ? '#ffd54a' : '#ffffff')};
  font-size: 14px;
  font-weight: ${p => (p.$promo ? 800 : 700)};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0 26px;
  position: relative;

  &::after {
    content: '•';
    position: absolute;
    right: -3px;
    color: rgba(255, 255, 255, 0.4);
  }

  @media (max-width: 480px) {
    font-size: 12px;
    padding: 0 18px;
  }
`;

/* Reusable scrolling ticker — duplicates its items for a seamless loop.
   Items may be plain strings or { text, promo } objects (promo = highlighted). */
const Marquee = ({ items, duration, variant }) => {
  const norm = items.map(it => (typeof it === 'string' ? { text: it } : it));
  const loop = [...norm, ...norm];
  return (
    <CategoryStrip $variant={variant}>
      <MarqueeTrack $duration={duration}>
        {loop.map((it, i) => (
          <CategoryName key={`${it.text}-${i}`} $promo={it.promo} aria-hidden={i >= norm.length}>
            {it.text}
          </CategoryName>
        ))}
      </MarqueeTrack>
    </CategoryStrip>
  );
};

const FALLBACK_CATEGORIES = ['Mens Care', 'Glam & Glow', 'Home Cleaning'];

// First-time-user coupon promo — shown only in the top ticker.
const PROMO_ITEM = { text: '🎉 10% OFF for first-time users · Use code CCNEW10', promo: true };

const HomePage = () => {
  const { data, isLoading: loadingCats } = useCategories();
  const categories = toArray(data)
    .filter(it => it.categoryName !== 'CC Helpers')
    .filter(it => it.isLive !== false);

  // Category names for the in-page ticker.
  const stripNames = categories.length
    ? categories.map(c => c.categoryName ?? c.name).filter(Boolean)
    : FALLBACK_CATEGORIES;

  // Top ticker shows only the coupon promo, repeated so it fills the width and
  // loops seamlessly.
  const promoItems = Array.from({ length: 6 }, () => PROMO_ITEM);

  // Scroll-reveal: sections fade + rise as they enter the viewport, giving the
  // page a layered, "unfolding" feel instead of flat scrolling. Falls back to
  // fully-visible if IntersectionObserver is unavailable so content never hides.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('[data-reveal]'));
    if (els.length === 0) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('reveal-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' },
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [loadingCats]);

  return (
    <>
      <Header />

      {/* Scrolling ticker directly below the navbar */}
      <Marquee items={promoItems} duration={26} />

      <div>
        <div>
          <main>
            <Hero categories={categories} loading={loadingCats} />

            {/* Banner video area */}
            <div className="tp-banner-area">
              <div className="tp-banner-img">
                <video className="w-100" src="assets/img/home-01/video/v1.mp4" muted autoPlay loop />
              </div>
            </div>

            {/* <AdvertisementBanner /> */}

            {/* Category ticker (scrolls the other direction feel via speed) */}
            <Marquee items={stripNames} duration={30} variant="light" />

            <div className="reveal" data-reveal>
              <ServicesMiddle categories={categories} loading={loadingCats} />
            </div>

            <div className="reveal" data-reveal>
              <PopularServices />
            </div>
            <div className="reveal" data-reveal>
              <DiscountedServices />
            </div>
            <div className="reveal" data-reveal>
              <ServiceVideosSection />
            </div>

            {/* <Partners /> */}
            <DesignChooseArea />
            <div className="reveal" data-reveal>
              <WorkVideoArea />
            </div>
            <div className="reveal" data-reveal>
              <Testimonials />
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default HomePage;
